import * as pdfjsLib from 'pdfjs-dist/build/pdf.js';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { createWorker } from 'tesseract.js';

/* ================== 1. PDF 텍스트 추출 ================== */
pdfjsLib.GlobalWorkerOptions.workerSrc=pdfWorkerUrl;   // Vite 가 워커 파일을 해시 이름으로 내보내고 그 URL 을 준다

const RESULTS=[];
const norm=s=>String(s||"").replace(/\s+/g,"");
const fmtWon=n=>(n==null||isNaN(n))?"":Number(n).toLocaleString("ko-KR");
const parseAmt=s=>Number(String(s).replace(/[^\d]/g,""))||0;
const esc=s=>String(s??"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

/* 채무자 명칭 정리: 뒤에 딸려오는 주소/설명 문구 제거 → 상호(법인)만 남김 */
function cleanDebtor(raw){
  const d=norm(raw); if(!d)return "";
  const suf=/(주식회사|유한책임회사|유한회사|합자회사|합명회사|재단법인|사단법인|의료법인|학교법인|영농조합법인|협동조합|\(주\)|\(유\))/;
  const m=d.match(suf);
  if(m&&m.index>0) return d.slice(0, m.index+m[0].length);          // 접미형: 상호+법인격까지만
  if(m&&m.index===0){                                               // 접두형: 법인격+상호(주소/설명 전까지)
    const rest=d.slice(m[0].length);
    // 지역명(시·도)을 우선 경계로 사용 (단음절 행정단위는 상호 오절단 위험이 있어 제외)
    const nm=(rest.match(/^[가-힣A-Za-z0-9()]+?(?=서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충청|충북|충남|전라|전북|전남|경상|경북|경남|제주|번지|산\d|외\d|조에|의한|$)/)||[rest])[0];
    return m[0]+nm;
  }
  return d;
}

/* 채권최고액을 통화별로 분리 합산 (외화를 원화에 섞어 더하지 않기 위함) */
function amtByCurrency(mortgages){
  const by={};
  (mortgages||[]).forEach(m=>{ if(!m.maxAmt)return; const c=m.cur||"KRW"; by[c]=(by[c]||0)+m.maxAmt; });
  return by;
}
/* {KRW:1e8, USD:1e6} → "100,000,000 / USD 1,000,000" (KRW만이면 코드 생략) */
function fmtAmtByCur(by){
  const keys=Object.keys(by); if(!keys.length)return "";
  const onlyKRW=keys.length===1&&keys[0]==="KRW";
  keys.sort((a,b)=> a==="KRW"?-1:b==="KRW"?1:a.localeCompare(b));
  return keys.map(c=>(onlyKRW?"":c+" ")+Number(by[c]).toLocaleString("ko-KR")).join(" / ");
}
const hasForeignMort=r=>(r.mortgages||[]).some(m=>m.cur&&m.cur!=="KRW");

/* 파일 처리 상태 판정: 완료 / 검토 필요 / 인식 실패 */
function fileStatus(r, ocr){
  if(r.error) return {cls:"err", text:"인식 실패"};
  const w=r.warnings||[];
  if(!r.owners||!r.owners.length||w.some(x=>/양식을 인식하지 못/.test(x))) return {cls:"err", text:"인식 실패"};
  if(ocr||hasForeignMort(r)||w.length) return {cls:"rev", text:ocr?"검토 필요 (OCR)":"검토 필요"};
  return {cls:"ok", text:"완료"};
}

/* 텍스트 아이템을 y좌표 기준으로 줄 단위 재구성 */
function buildLines(items){
  const rows=[];
  for(const it of items){
    if(!it.str||!it.str.trim())continue;
    const y=it.transform[5], x=it.transform[4];
    let r=null;
    for(const q of rows){ if(Math.abs(q.y-y)<=3){r=q;break;} }
    if(!r){r={y,parts:[]};rows.push(r);}
    r.parts.push({x,s:it.str});
  }
  rows.sort((a,b)=>b.y-a.y);
  return rows.map(r=>r.parts.sort((a,b)=>a.x-b.x).map(p=>p.s).join(" ").replace(/\s+/g," ").trim());
}

/* tesseract.js 워커 풀 (파일 간 재사용). OCR 엔진은 스캔본에서만 지연 로드 (assets/vendor 내장) */
let TWORKERS=null;
/* tesseract.js 5.1.1 (npm). 지휘자는 번들에, 워커/WASM/언어데이터는 빌드가 dist/vendor 로 복사. 워커 안에서는 상대 경로가 풀리지 않으므로 절대 URL */
const TESS_BASE=new URL("/vendor/tesseract/5.1.1/", location.href).href;   // vite.config.js 의 정적 복사 대상
async function getTessWorkers(){
  if(!TWORKERS){
    const n=Math.max(1, Math.min(3, (navigator.hardwareConcurrency||4)-2));
    TWORKERS=await Promise.all(Array.from({length:n},()=>createWorker("kor", 1, {
      workerPath: TESS_BASE+"worker.min.js",
      corePath:   TESS_BASE+"core/",      // 워커가 SIMD 지원을 감지해 simd-lstm 또는 lstm 변형을 고른다
      langPath:   TESS_BASE+"lang/",      // kor.traineddata.gz
      workerBlobURL: false                // blob 대신 같은 출처 Worker. 동작 동일, CSP 단순
    })));
  }
  return TWORKERS;
}
/* 캔버스 목록을 OCR (워커 풀로 병렬) */
async function ocrCanvases(canvases, onProgress){
  const workers=await getTessWorkers();
  const out=new Array(canvases.length); let next=0, done=0;
  await Promise.all(workers.map(async w=>{
    while(true){
      const idx=next++; if(idx>=canvases.length)break;
      const {data}=await w.recognize(canvases[idx]);
      out[idx]=data.text; done++;
      if(onProgress)onProgress(done,canvases.length);
    }
  }));
  return out;
}

/* ===== PaddleOCR (PP-OCRv5 한국어), 스캔본 고정밀 OCR (1순위) =====
   ONNX Runtime Web + WebGPU. tesseract보다 문자 인식(등록번호, 금액, 면적)이 정확.
   탐지 maxSideLength를 640→1280으로 올려 좁은 '순위번호' 열의 작은 숫자까지 인식.
   코드(ppu-paddle-ocr)는 npm 번들, 모델 3개(17.8MB)는 public/vendor/paddle 에 커밋해 우리 서버에서 받는다.
   ONNX 런타임(JS, WASM)만 jsdelivr 에서 버전 고정으로 받는다: WebGPU 빌드의 WASM 이 27.8MB 로
   Pages 파일 한도(25MiB)를 넘어 내장할 수 없고, CPU WASM 은 실측 A4 한 페이지 16초로 화면이 멎는다. CLAUDE.md 4절 예외.
   실패 시 tesseract 로 폴백 */
let PPSVC=null, PPLOAD=null;
async function getPaddle(){
  if(PPSVC) return PPSVC;
  if(!PPLOAD) PPLOAD=(async()=>{
    const m=await import("ppu-paddle-ocr/web");   // Vite 가 별도 청크로. onnxruntime-web 은 vite.config.js 별칭으로 jsdelivr
    const PM=new URL("/vendor/paddle/v5-korean-mobile/", location.href).href;
    const svc=new m.PaddleOcrService({
      model:{ detection: PM+"PP-OCRv5_mobile_det_infer.onnx",
              recognition: PM+"korean_PP-OCRv5_mobile_rec_infer.onnx",
              charactersDictionary: PM+"ppocrv5_korean_dict.txt" },   // V5_KOREAN_MOBILE_MODEL 과 같은 파일, 출처는 우리 서버
      detection:{ maxSideLength:1280, minimumAreaThreshold:20 }
    });
    await svc.initialize();
    PPSVC=svc; return svc;
  })();
  return PPLOAD;
}
/* 인식된 단어 박스를 y좌표로 묶고 x로 정렬해 시각적 '행'으로 재조립.
   좌측 순위번호(1,2,3…) 열이 등기목적 본문 행에 결합되어 파서가 항목을 분리할 수 있게 함 */
function boxLines(segs){
  const rows=[];
  for(const it of segs){
    if(!it||!it.text||!it.text.trim()||!it.box)continue;
    const y=it.box.y, x=it.box.x, h=it.box.height||30;
    let r=rows.find(q=>Math.abs(q.y-y)<=Math.max(14,h*0.5));
    if(!r){r={y,parts:[]};rows.push(r);}
    r.parts.push({x,s:it.text});
  }
  rows.sort((a,b)=>a.y-b.y);
  return rows.map(r=>r.parts.sort((a,b)=>a.x-b.x).map(p=>p.s).join(" ").replace(/\s+/g," ").trim()).filter(Boolean);
}
/* 인식 속도 최적화: 장변을 maxSide로 다운스케일. 탐지는 어차피 1280으로 정규화되고,
   1240px 해상도에서 등록번호·순위번호까지 정확히 인식됨을 검증 → 정확도 손실 없이 픽셀 수만 감소 */
function fitCanvas(cv, maxSide){
  const long=Math.max(cv.width, cv.height);
  if(long<=maxSide) return cv;
  const s=maxSide/long;
  const c2=document.createElement("canvas");
  c2.width=Math.round(cv.width*s); c2.height=Math.round(cv.height*s);
  c2.getContext("2d").drawImage(cv,0,0,c2.width,c2.height);
  return c2;
}
/* PaddleOCR로 캔버스 목록 OCR → 행 재조립된 lines 반환 (WebGPU 단일 디바이스이므로 순차 처리) */
async function ocrCanvasesPaddle(canvases, onProgress){
  const svc=await getPaddle();
  let lines=[], done=0;
  for(const cv of canvases){
    const r=await svc.recognize(fitCanvas(cv,1300),{noCache:true});   // 다운스케일 + 페이지별 캐시 오인 방지
    lines=lines.concat(boxLines((r.lines||[]).flat()));
    done++; if(onProgress)onProgress(done,canvases.length);
  }
  return lines;
}

/* 텍스트 PDF는 직접 추출, 스캔 PDF는 페이지를 캔버스로 렌더링 후 OCR — {lines, ocr} 반환 */
async function extractPdfLines(file, onProgress){
  const buf=await file.arrayBuffer();
  const doc=await pdfjsLib.getDocument({data:buf.slice(0)}).promise;   // pdf.js가 버퍼를 소비하므로 복사본 전달
  const lines=[]; let textItems=0;
  for(let p=1;p<=doc.numPages;p++){
    const page=await doc.getPage(p);
    const tc=await page.getTextContent();
    textItems+=tc.items.length;
    lines.push(...buildLines(tc.items));
  }
  if(textItems>20){ doc.destroy(); return {lines, ocr:false}; }
  // 텍스트 레이어 없음 → 스캔본: 각 페이지를 캔버스로 렌더링 후 OCR
  // (JPEG 직접추출과 달리 PNG/타일/마스크/회전/다중이미지 스캔까지 처리)
  const canvases=[];
  for(let p=1;p<=doc.numPages;p++){
    const page=await doc.getPage(p);
    const vp=page.getViewport({scale:2});                 // 해상도 2배 렌더링 (인식률 확보)
    const cv=document.createElement("canvas");
    cv.width=Math.ceil(vp.width); cv.height=Math.ceil(vp.height);
    await page.render({canvasContext:cv.getContext("2d"), viewport:vp}).promise;
    canvases.push(cv);
  }
  doc.destroy();
  if(!canvases.length) throw new Error("페이지를 렌더링하지 못했습니다 (지원하지 않는 형식)");
  let olines;
  try{
    olines=await ocrCanvasesPaddle(canvases, onProgress);       // PaddleOCR 우선
    if(!olines.length) throw new Error("PaddleOCR 결과 없음");
  }catch(e){
    console.warn("PaddleOCR 사용 불가, tesseract로 폴백합니다:", e);
    const texts=await ocrCanvases(canvases, onProgress);
    olines=texts.flatMap(t=>t.split("\n").map(s=>s.replace(/\s+/g," ").trim()).filter(Boolean));
  }
  return {lines:olines, ocr:true};
}

/* ================== 2. 등기부 해석 ================== */
const RISK_RE=/(가압류|압류|가처분|경매개시결정|임의경매|강제경매|신탁|환매특약|가등기)/;
const PURP_KEY=/(소유권|저당|전세권|지상권|지역권|임차권|압류|경매|가처분|신탁|가등기|환매|말소|변경|경정|이전|보존|설정|회복)/;
const NOISE_RE=[
  /^열람일시/, /^출력일시/, /^발행일/, /^발행번호/, /^\d+\s*\/\s*\d+$/, /수수료/, /인터넷등기소/,
  /관할등기소/, /실선으로 그어진 부분은 말소사항/, /지연이자에 관한 사항/, /기록사항 없는/,
  /^순위번호\s+등기\s*목적/, /^표시번호/, /^등기사항전부증명서/, /^\[\s*(토지|건물|집합건물)\s*\]/,
  /^고유번호/, /이 증명서는/, /^열\s*람\s*용/, /문서 하단의 바코드/, /전자적으로 위/,
  /발급확인번호/, /위.변조 여부를 확인/, /법원행정처/, /전산운영책임관/, /컬러 또는 흑백/,
  /\d{12,}/                                    // OCR 잡음: 복사방지 패턴 등 초장문 숫자열
];
/* OCR 잡음 판정: 한글-숫자 뒤범벅(복사방지 패턴 오인식)
   단, 날짜/금액/번호의 정상 단위(년월일호원번분 등)가 붙은 숫자는 제외 */
function isGibberish(ln){
  const s=norm(ln);
  if(s.length<25)return false;
  const mash=(s.match(/[가-힣]\d{2,}(?![년월일호원번분,.\d])[가-힣]/g)||[]).length;
  return mash>=3;
}
const isNoise=ln=>NOISE_RE.some(re=>re.test(ln))||isGibberish(ln);

/* 구(區) 내부를 [순위번호 + 등기목적 + 본문] 단위로 분리 */
function parseEntries(lines){
  const entries=[]; let cur=null;
  for(const ln of lines){
    if(isNoise(ln))continue;
    const m=ln.match(/^(\d+(?:-\d+)?)\s+(\S+)(.*)$/);
    if(m && PURP_KEY.test(m[2]) && /[가-힣]/.test(m[2])){
      cur={rank:m[1], purpose:m[2], text:ln};
      entries.push(cur);
    }else if(cur){
      cur.text+=" "+ln;
    }
  }
  return entries;
}

/* 말소등기로 실효된 순위번호 집합
   "1번근저당권설정, 2번근저당권설정, 3번근저당권설정등기말소"처럼 나열형+줄바꿈 분리도
   '말소' 출현 지점 앞 90자 창에서 N번 참조를 모두 수집하는 방식으로 처리 */
function cancelledRanks(entries){
  const set=new Set();
  for(const e of entries){
    const t=e.text;
    let idx=0;
    while(true){
      const k=t.indexOf("말소",idx); if(k<0)break;
      if(!/^말소\s*회복/.test(t.slice(k,k+8))){
        const win=t.slice(Math.max(0,k-90),k);
        [...win.matchAll(/(\d+(?:-\d+)?)\s*번/g)].forEach(x=>set.add(x[1]));
        // 등기목적 자체가 말소등기면 그 순위번호도 권리 아님
        if(norm(t).slice(0,45).includes("말소")) set.add(e.rank);
      }
      idx=k+2;
    }
  }
  return set;
}

const firstDate=t=>{const m=t.match(/(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일/);return m?`${m[1]}-${String(m[2]).padStart(2,"0")}-${String(m[3]).padStart(2,"0")}`:"";};

/* 소유자/공유자/근저당권자 등 당사자 추출: "역할 [지분 n분의 m] 이름 000000-*******" */
function extractParties(text, roleRe){
  const out=[];
  // 접수번호("제N호")가 OCR 줄 순서상 이름 앞에 끼는 경우가 있어 제거 후 해석
  const t=text.replace(/제\s*[\d,]+호/g," ").replace(/\s+/g," ");
  // 역할 뒤 공백은 선택(\s*): OCR로 "근저당권자주식회사하나은행"처럼 붙어 나오는 경우 대응
  const re=new RegExp(`(${roleRe})\\s*(?:지분\\s+(\\d+[\\d,.]*)\\s*분의\\s*(\\d+[\\d,.]*)\\s+)?([가-힣A-Za-z0-9()&.\\-]+(?:\\s[가-힣A-Za-z0-9()&.\\-]+)*?)\\s+(\\d{6}[-]?[\\d*]{1,7})`,"g");
  let m;
  while((m=re.exec(t))!==null){
    const name=m[4].replace(/\s+/g,"").replace(/^지분/,"");
    out.push({role:m[1], share:m[2]?`${m[2]}분의 ${m[3]}`:"", name, regNo:m[5]});
  }
  if(!out.length){ // 등록번호 없는 옛 양식: 역할 뒤 첫 어절만
    const re2=new RegExp(`(${roleRe})\\s*([가-힣A-Za-z0-9()&.\\-]+)`,"g");
    while((m=re2.exec(t))!==null) out.push({role:m[1], share:"", name:m[2], regNo:""});
  }
  return out;
}

/* 갑구 해석 → { owners:[], rights:[] } */
function parseGapgu(entries){
  const cancelled=cancelledRanks(entries);
  let owners=[]; const rights=[];
  for(const e of entries){
    if(cancelled.has(e.rank))continue;
    const p=norm(e.purpose);
    if(/^소유권(일부)?이전$|^소유권보존$|^공유자전원지분|^\d*번?공유자.*지분.*이전/.test(p)){
      const ps=extractParties(e.text,"소유자|공유자|수탁자");
      const cause=(e.text.match(/(매매|상속|증여|신탁재산의귀속|신탁|경락|낙찰|공매|교환|현물출자|합병|분할|판결|공공용지의협의취득|수용)/)||[])[1]||"";
      const price=(norm(e.text).match(/거래가액금?([\d,]+)원/)||[])[1]||"";
      const isPartial=/일부/.test(p)&&!/전원|전부/.test(p);
      const rec={rank:e.rank, date:firstDate(e.text), cause, price:price?parseAmt(price):null, parties:ps, partial:isPartial};
      if(isPartial){ owners.push(rec); }   // 일부이전은 누적 (지분원장 아님 → 요약 없으면 수동검토 격상)
      else{ owners=[rec]; }                // 보존/전부이전은 대체
    }else if(RISK_RE.test(p)){
      const holder=(e.text.match(/(?:채권자|권리자|신탁원부|청구권자)\s+([가-힣A-Za-z0-9()&.\-]+)/)||[])[1]||"";
      rights.push({rank:e.rank, kind:(p.match(RISK_RE)||[])[1]||e.purpose, date:firstDate(e.text), holder, raw:e.text});
    }
  }
  /* 등기명의인표시변경(상호변경/명칭변경): 소유자 명칭을 최신 상호로 갱신.
     예: "…의 성명(명칭) 대진첨단소재주식회사" → 소유자명을 대진첨단소재주식회사로.
     명의변경 항목이 별도 항목이든 소유권 항목에 흡수되든 무관하게 전체 텍스트에서 탐지 */
  const CORP="주식회사|유한회사|유한책임회사|합자회사|합명회사|재단법인|사단법인|의료법인|학교법인|영농조합법인|농업회사법인|협동조합|\\(주\\)|\\(유\\)";
  const allTxt=norm(entries.filter(e=>!cancelled.has(e.rank)).map(e=>e.text).join(" "));
  // 등기원인 "상호변경/개명" 바로 뒤의 신상호(법인)를 추출. (성명(명칭) 앵커는 컬럼 재배열 잡음이 끼어 부정확)
  const ncRe=new RegExp(`(?:상호변경|개명)\\s*([가-힣A-Za-z()]{2,20}?(?:${CORP}))`,"g");
  let nc, newName=null; while((nc=ncRe.exec(allTxt))!==null) newName=nc[1];   // 여러 번이면 최신(마지막)
  if(newName && owners.length===1 && owners[0].parties.length===1){
    owners[0].parties[0].name=newName;                                // 단독소유 상호변경 반영
  }
  return {owners, rights, cancelled, hadPartial:owners.some(o=>o.partial)};
}

/* 을구 해석 → { mortgages:[], others:[] } */
function parseEulgu(entries){
  const cancelled=cancelledRanks(entries);
  const mortgages=[]; const others=[];
  for(const e of entries){
    if(cancelled.has(e.rank))continue;
    const p=norm(e.purpose), nt=norm(e.text);
    if(/근저당권설정$/.test(p)&&!/변경|이전|말소/.test(p)){
      let maxAmt=parseAmt((nt.match(/채권최고액금?([\d,]+)원/)||[])[1]||""), cur="KRW";
      if(!maxAmt){ // 외화 채권최고액 (미화 금N달러 등)
        const fm=nt.match(/채권최고액(미화|유로화|엔화|위안화)?금?([\d,]+)(달러|유로|엔|위안)/);
        if(fm){maxAmt=parseAmt(fm[2]); cur={"달러":"USD","유로":"EUR","엔":"JPY","위안":"CNY"}[fm[3]]||"FX";}
      }
      const debtor=cleanDebtor((nt.match(/채무자([가-힣A-Za-z0-9()&.\-]{1,25}?)(?=서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충청|충북|충남|전라|전북|전남|경상|경북|경남|제주|\d{6}|공동담보|근저당권자|근저당권|채권최고액|의한|조에|외\d|$)/)||[])[1]||"");
      const hp=extractParties(e.text,"근저당권자");
      let joint=(e.text.match(/공동담보\s*(목록\s*제?\s*[\d-]+호)/)||[])[1]||"";
      if(!joint&&/공동담보/.test(e.text)) joint=(e.text.match(/공동담보\s+(\S+(?:\s\S+){0,11})/)||[])[1]||"있음";
      mortgages.push({rank:e.rank, date:firstDate(e.text), maxAmt, cur, debtor,
        holder:hp.length?hp[0].name:"", holderReg:hp.length?hp[0].regNo:"", joint,
        note:cur!=="KRW"?`외화(${cur})`:"", raw:e.text});
    }else if(/^\d+번근저당권.*(변경|경정)/.test(p)){
      const ref=(p.match(/^(\d+(?:-\d+)?)번/)||[])[1];
      const t=mortgages.find(x=>x.rank===ref);
      const newAmt=parseAmt((nt.match(/채권최고액금?([\d,]+)원/)||[])[1]||"");
      if(t&&newAmt){t.maxAmt=newAmt; t.note=(t.note?t.note+" / ":"")+`${e.rank}번 변경 반영`;}
    }else if(/^\d+번근저당권이전/.test(p)){
      const ref=(p.match(/^(\d+(?:-\d+)?)번/)||[])[1];
      const t=mortgages.find(x=>x.rank===ref);
      const hp=extractParties(e.text,"근저당권자");
      if(t&&hp.length){t.holder=hp[0].name; t.holderReg=hp[0].regNo; t.note=(t.note?t.note+" / ":"")+`${e.rank}번 이전 반영`;}
    }else if(/(전세권설정|지상권설정|지역권설정|임차권설정|주택임차권|상가건물임차권)/.test(p)){
      const kind=(p.match(/(전세권|지상권|지역권|임차권|주택임차권|상가건물임차권)/)||[])[1];
      const amt=parseAmt((nt.match(/(?:전세금|임차보증금)금?([\d,]+)원/)||[])[1]||"");
      const hp=extractParties(e.text,"전세권자|지상권자|지역권자|임차권자");
      others.push({rank:e.rank, kind, date:firstDate(e.text), amt, holder:hp.length?hp[0].name:"", raw:e.text});
    }
  }
  return {mortgages, others, cancelled};
}

/* 주요 등기사항 요약 페이지 해석 */
function parseSummary(lines){
  const idx1=lines.findIndex(l=>/^1\./.test(norm(l))&&norm(l).includes("소유지분현황"));
  const idx2=lines.findIndex(l=>/^2\./.test(norm(l))&&norm(l).includes("소유지분을제외한"));
  const idx3=lines.findIndex(l=>/^3\./.test(norm(l))&&(norm(l).includes("저당권")||norm(l).includes("전세권")));
  const end=lines.length;
  const slice=(a,b)=>a<0?[]:lines.slice(a+1, b<0?end:b).filter(l=>!isNoise(l));
  /* 소유지분현황: 이름이 열 폭 때문에 줄바꿈으로 쪼개지는 경우("대화제약주식회" / "사 (소유자)")
     등록번호가 있는 줄을 레코드로 잡고, 역할 표기만 있는 조각 줄의 이름을 이어붙임 */
  const owners=[]; let pending=null;
  for(const ln of slice(idx1, idx2<0?idx3:idx2)){
    if(/등기명의인|등록번호\s+최종지분/.test(ln))continue;
    const hasRole=ln.match(/\(\s*(소유자|공유자)\s*\)/);
    const hasReg=ln.match(/(\d{6}-[\d*]{1,7})/);
    const shareOf=s=>{const m=s.match(/단독소유|\d+[\d,.]*\s*분의\s*\d+[\d,.]*/);return m?m[0].replace(/\s+/g," "):"";};
    if(hasRole&&hasReg){
      const name=norm(ln.slice(0,Math.min(ln.indexOf(hasRole[0]),ln.indexOf(hasReg[1]))).replace(/\(.*$/,""));
      if(name)owners.push({name, role:hasRole[1], regNo:hasReg[1], share:shareOf(ln)});
      pending=null;
    }else if(hasReg){
      pending={name:norm(ln.slice(0,ln.indexOf(hasReg[1]))), role:"", regNo:hasReg[1], share:shareOf(ln)};
      owners.push(pending);
    }else if(hasRole&&pending){
      pending.name+=norm(ln.slice(0,ln.indexOf(hasRole[0])));
      pending.role=hasRole[1]; pending=null;
    }
  }
  const gapRights=[];
  for(const ln of slice(idx2, idx3)){
    const m=ln.match(/^(\d+(?:-\d+)?)\s+(\S+)/);
    if(m&&RISK_RE.test(m[2])) gapRights.push({rank:m[1], kind:(m[2].match(RISK_RE)||[])[1]||m[2], date:firstDate(ln), holder:"", raw:ln});
  }
  const eulRanks=new Set(); const eulRows=[];
  const t3=slice(idx3,-1).join(" ");
  const re3=/(\d+(?:-\d+)?)\s+((?:근저당권|저당권|전세권|지상권|임차권|주택임차권|상가건물임차권)\S*)/g;
  let m3;
  while((m3=re3.exec(t3))!==null){
    eulRanks.add(m3[1].split("-")[0]);
    const win=t3.slice(m3.index, m3.index+220);
    eulRows.push({rank:m3[1], kind:m3[2], maxAmt:parseAmt((norm(win).match(/(?:채권최고액|전세금)금?([\d,]+)원/)||[])[1]||""),
      holder:(norm(win).match(/(?:근저당권자|전세권자|임차권자)([가-힣A-Za-z0-9()&.\-]{1,25}?)(?=\d|채권|주식회사$|$)/)||[])[1]||""});
  }
  return {found:idx1>=0||idx3>=0, owners, gapRights, eulRanks, eulRows, has3:idx3>=0};
}

/* 표제부에서 면적 추출 */
function parseAreas(lines, kind){
  const nAll=lines.map(l=>norm(l));
  const areasOf=arr=>arr.flatMap(l=>[...l.matchAll(/([\d,]+(?:\.\d+)?)(?:㎡|m2|m²)/g)].map(m=>parseFloat(m[1].replace(/,/g,""))));
  let areaMain=null, areaText="", landRight="";
  if(kind==="집합건물"){
    const s=nAll.findIndex(l=>l.includes("전유부분의건물의표시"));
    const e=nAll.findIndex(l=>l.includes("대지권의표시"));
    const seg=nAll.slice(s<0?0:s, e<0?nAll.length:e);
    const as=areasOf(seg);
    if(as.length){areaMain=as[as.length-1]; areaText=`전유 ${areaMain}㎡`;}
    else{ // OCR에서 ㎡가 깨진 경우: 구조 표기 뒤 소수점 숫자
      const fm=seg.join(" ").match(/(?:구조|조적조|목조|철골)[^\d]{0,4}([\d,]+\.\d{1,2})/);
      if(fm){areaMain=parseFloat(fm[1].replace(/,/g,"")); areaText=`전유 ${areaMain}㎡ (OCR 추정)`;}
    }
    if(e>=0){
      const seg2=lines.slice(e, e+10);   // 대지권 표시 직후 몇 줄만 (갑구의 "지분 N분의 M" 오인 방지)
      for(let i=0;i<seg2.length;i++){
        // (?![\d년]) : OCR로 비율 숫자가 유실되고 날짜가 바로 붙은 경우("분의 2020년...") 오인 방지
        const dm=seg2[i].match(/([\d,]+(?:\.\d+)?)\s*분의\s*([\d,]+(?:\.\d+)?)(?![\d년])/);
        if(dm){landRight=`${dm[1]}분의 ${dm[2]}`;break;}
        // 분자가 다음 줄로 넘어간 경우("788.8분의 2020년…" / 다음 줄 "30.6474 …"): 다음 줄 앞머리 숫자를 분자로
        const dmDen=seg2[i].match(/([\d,]+(?:\.\d+)?)\s*분의/);
        if(dmDen){
          const nx=(seg2[i+1]||"").match(/^\s*([\d,]+(?:\.\d+)?)(?![\d년])/);
          if(nx){landRight=`${dmDen[1]}분의 ${nx[1]}`;break;}
        }
      }
    }
  }else if(kind==="토지"){
    const as=areasOf(nAll);
    if(as.length){areaMain=as[as.length-1]; areaText=`${areaMain}㎡`;}
    else{ // OCR 폴백: 지목 뒤의 숫자
      const fm=nAll.join(" ").match(/(?:^|\s)(대|전|답|임야|잡종지|공장용지|창고용지|도로|주차장)([\d,]+(?:\.\d+)?)/);
      if(fm){areaMain=parseFloat(fm[2].replace(/,/g,"")); areaText=`${fm[1]} ${areaMain}㎡ (OCR 추정)`;}
    }
    const jm=nAll.join("").match(/(대|전|답|임야|잡종지|공장용지|창고용지|도로|주차장|과수원|목장용지|학교용지|주유소용지|체육용지|유지|하천|구거|철도용지|제방|양어장|수도용지|묘지|염전|광천지|사적지|종교용지|공원|유원지)(?=[\d,]+(?:\.\d+)?(?:㎡|m2|m²))/);
    if(jm&&!areaText.includes(jm[1])) areaText=`${jm[1]} ${areaText}`;
  }else{ // 건물
    const fl=[...nAll.join(" ").matchAll(/(\d+층|지하\d+층|옥탑\d*층?)([\d,]+(?:\.\d+)?)㎡/g)];
    if(fl.length){
      const seen={}; fl.forEach(m=>{seen[m[1]]=parseFloat(m[2].replace(/,/g,""));}); // 층별 마지막 값
      areaMain=Object.values(seen).reduce((a,b)=>a+b,0);
      areaMain=Math.round(areaMain*100)/100;
      areaText=`연면적(합산) ${areaMain}㎡ / ${Object.keys(seen).length}개층`;
    }else{
      const as=areasOf(nAll);
      if(as.length){areaMain=as[as.length-1]; areaText=`${areaMain}㎡`;}
    }
  }
  return {areaMain, areaText, landRight};
}

/* 파일 1건 종합 해석 */
function parseRegistry(lines, fname){
  const R={file:fname, uniqueNo:"", kind:"", address:"", viewedAt:"",
    areaMain:null, areaText:"", landRight:"", owners:[], ownerHistory:[],
    mortgages:[], eulOthers:[], gapRights:[], warnings:[], hasSummary:false};

  for(const ln of lines){
    if(!R.uniqueNo){const m=ln.match(/고유번호\s*[:：]?\s*(\d{4}-\d{4}-\d{6})/); if(m)R.uniqueNo=m[1];}
    if(!R.kind){const m=ln.match(/\[\s*(토지|건물|집합건물)\s*\]\s*(.+)/); if(m){R.kind=m[1]; R.address=m[2].replace(/고유번호.*$/,"").trim();}}
    if(!R.viewedAt){const m=ln.match(/(?:열람일시|발급일|발행일)\s*[:：]?\s*(\d{4}[년\/.-]\s*\d{1,2}[월\/.-]\s*\d{1,2}일?[\d\s:시분초]*)/); if(m)R.viewedAt=m[1].trim().replace(/\s+\d+$/,"");}   // 꼬리의 페이지 번호("... 1/5"의 1) 제거
  }

  /* 섹션 앵커: OCR로 '갑구' 등 표제가 깨져도 괄호 설명문으로 인식 */
  const nIdx=re=>lines.findIndex(l=>re.test(norm(l)));
  const iPyo=nIdx(/【표제부】|동의건물의표시|토지의표시\)|전유부분의건물의표시/);
  const iGap=nIdx(/【갑구】|소유권에관한사항/);
  const iEul=nIdx(/【을구】|소유권이외의권리에관한사항/);
  const iSum=nIdx(/주요등기사항요약/);
  const endMain=iSum>=0?iSum:lines.length;
  if(iPyo<0&&iGap<0){R.warnings.push("등기부 양식을 인식하지 못했습니다"); return R;}

  /* 섹션 경계. OCR로 헤더가 깨진 경우 등기목적 유형으로 경계를 추정:
     갑구에는 저당/전세/지상/임차 목적이 존재하지 않음 */
  const EUL_PURP=/근[저져]당|저당권|전세권|지상권|지역권|임차권/;
  const iBody=iGap>=0?iGap:(iPyo<0?0:iPyo);                 // 갑구 헤더가 없으면 표제부부터 훑음
  const pyoLines=lines.slice(iPyo<0?0:iPyo, iGap>=0?iGap:(iEul>=0?iEul:endMain));
  const gapRegion=lines.slice(iBody, iEul>=0?iEul:endMain);
  const eulRegion=iEul>=0?lines.slice(iEul, endMain):[];

  Object.assign(R, parseAreas(pyoLines, R.kind||"건물"));

  let gapEntries=parseEntries(gapRegion), eulEntries=parseEntries(eulRegion);
  if(iEul<0){
    const k=gapEntries.findIndex(e=>EUL_PURP.test(norm(e.purpose)));
    if(k>=0){ eulEntries=gapEntries.slice(k); gapEntries=gapEntries.slice(0,k); }
  }
  const gap=parseGapgu(gapEntries);
  const eul=parseEulgu(eulEntries);
  R.ownerHistory=gap.owners;

  const sum=iSum>=0?parseSummary(lines.slice(iSum)):{found:false};
  R.hasSummary=!!sum.found;

  if(sum.found&&sum.owners.length){
    R.owners=sum.owners;
  }else{
    R.owners=gap.owners.flatMap(o=>o.parties.map(p=>({name:p.name, role:p.role, regNo:p.regNo,
      share:p.share||(p.role==="소유자"?"단독소유":""), cause:o.cause, date:o.date})));
    if(!sum.found) R.warnings.push("요약 페이지 없음: 갑구/을구 직접 해석 (말소 상계 추정)");
    // #7 요약 없음 + 일부지분 이전: 지분원장 방식이 아니라 과거 소유자가 남을 수 있음 → 수동 검토 필수
    if(!sum.found && gap.hadPartial) R.warnings.push("일부지분 이전 감지: 소유자/지분 자동확정 불가 - 지분 내역 수동 검토 필수");
  }
  if(!R.owners.length) R.warnings.push("소유자 인식 실패: 원본 확인 필요");

  if(sum.found&&sum.has3){
    R.mortgages=eul.mortgages.filter(m=>sum.eulRanks.has(m.rank.split("-")[0]));
    for(const sr of sum.eulRows){ // 본문 해석에서 놓친 유효 항목 보강
      if(/근저당/.test(sr.kind)&&!R.mortgages.some(m=>m.rank.split("-")[0]===sr.rank.split("-")[0]))
        R.mortgages.push({rank:sr.rank, date:"", maxAmt:sr.maxAmt, debtor:"", holder:sr.holder, joint:"", note:"요약 페이지 기준", raw:""});
    }
    R.eulOthers=eul.others.filter(o=>sum.eulRanks.has(o.rank.split("-")[0]));
  }else if(sum.found&&!sum.has3){
    R.mortgages=[]; R.eulOthers=[];   // 요약에 을구 섹션 없음 = 유효한 을구 권리 없음
  }else{
    R.mortgages=eul.mortgages; R.eulOthers=eul.others;
  }

  R.gapRights=(sum.found&&sum.gapRights)?sum.gapRights:gap.rights;
  if(sum.found&&!sum.gapRights.length&&gap.rights.length) R.gapRights=[]; // 요약 기준 우선

  if(R.gapRights.length) R.warnings.push("갑구 권리제한 있음: "+R.gapRights.map(r=>r.kind).join(", "));
  R.mortgages.forEach(m=>{if(!m.maxAmt)R.warnings.push(`근저당 ${m.rank}번 채권최고액 인식 실패`);});
  return R;
}

/* ================== 3. 업로드 및 화면 ================== */
const drop=document.getElementById("drop"), fileIn=document.getElementById("fileIn"), flist=document.getElementById("flist");
["dragenter","dragover"].forEach(ev=>drop.addEventListener(ev,e=>{e.preventDefault();drop.classList.add("on");}));
["dragleave","drop"].forEach(ev=>drop.addEventListener(ev,e=>{e.preventDefault();drop.classList.remove("on");}));
drop.addEventListener("drop",e=>handleFiles(e.dataTransfer.files));
fileIn.addEventListener("change",()=>{handleFiles(fileIn.files); fileIn.value="";});

function toggleGuide(show){
  document.getElementById("guide").style.display=show?"":"none";
  sessionStorage.setItem("registry.guide", show?"1":"0");
}
if(sessionStorage.getItem("registry.guide")==="0") toggleGuide(false);

async function handleFiles(fs){
  const pdfs=[...fs].filter(f=>/\.pdf$/i.test(f.name));
  if(!pdfs.length){alert("PDF 파일을 선택하세요."); return;}
  for(const f of pdfs){
    const it=document.createElement("div"); it.className="fitem";
    it.innerHTML=`<span class="st run">해석 중</span><span class="fn">${esc(f.name)}</span>`;
    flist.appendChild(it);
    const st=it.querySelector(".st");
    try{
      const ex=await extractPdfLines(f,(d,t)=>{st.textContent=`OCR ${d}/${t}p`;});
      const r=parseRegistry(ex.lines, f.name);
      r._lines=ex.lines;                       // 원문 보관 (디버그/원문 대조용)
      if(ex.ocr){ r.ocr=true; r.warnings.unshift("스캔본 OCR 해석: 금액과 명칭을 원본과 대조하세요"); }
      RESULTS.push(r);
      const stt=fileStatus(r, ex.ocr);
      st.className="st "+stt.cls;
      it.classList.toggle("rev", stt.cls==="rev");
      st.textContent=stt.text;
    }catch(err){
      RESULTS.push({file:f.name, error:String(err&&err.message||err), owners:[], mortgages:[], eulOthers:[], gapRights:[], warnings:["PDF 해석 오류: "+String(err&&err.message||err)]});
      it.querySelector(".st").className="st err";
      it.querySelector(".st").textContent="오류";
    }
    renderTable();
  }
}

const stripCorp=s=>norm(s).replace(/주식회사|\(주\)|㈜|유한책임회사|유한회사|\(유\)|합자회사|합명회사|재단법인|사단법인/g,"");
function coNames(){
  return document.getElementById("coName").value.split(",").map(stripCorp).filter(Boolean);
}
/* 회사명 대조: exact(법인격 제거 후 완전일치) / similar(부분일치, 확인필요) / none(불일치) / na(대상없음) */
function ownerMatch(r){
  const cos=coNames();
  if(!cos.length||!r.owners||!r.owners.length) return "na";
  const names=r.owners.map(o=>stripCorp(o.name)).filter(Boolean);
  if(names.some(n=>cos.some(c=>n===c))) return "exact";
  if(names.some(n=>cos.some(c=>n.includes(c)||c.includes(n)))) return "similar";
  return "none";
}

function renderTable(){
  const card=document.getElementById("resCard"), tb=document.getElementById("resB");
  if(!RESULTS.length){card.style.display="none"; return;}
  card.style.display="";
  document.getElementById("resCnt").textContent=`${RESULTS.length}건`;
  tb.innerHTML="";
  RESULTS.forEach((r,i)=>{
    const om=ownerMatch(r);
    const warns=[...(r.warnings||[])];
    if(om==="none") warns.unshift("소유자가 입력한 회사명과 불일치");
    else if(om==="similar") warns.unshift("회사명 부분일치(완전일치 아님): 확인 필요");
    const flag=om==="none"||(r.gapRights&&r.gapRights.length)||r.error;
    const totTxt=fmtAmtByCur(amtByCurrency(r.mortgages));
    const holders=[...new Set((r.mortgages||[]).map(m=>m.holder).filter(Boolean))].join(", ");
    const joints=[...new Set((r.mortgages||[]).map(m=>m.joint).filter(Boolean))].join(", ");
    const ownersTxt=(r.owners||[]).map(o=>`${o.name}${o.share?` (${o.share})`:""}`).join(", ");
    const tr=document.createElement("tr");
    tr.className="main"+(flag?" flag":"");
    tr.innerHTML=`
      <td class="ctr">${i+1}</td>
      <td class="ctr">${esc(r.kind||"-")}</td>
      <td>${esc(r.address||r.file)}<div class="hint">${esc(r.file)}${r.uniqueNo?" | "+esc(r.uniqueNo):""}</div></td>
      <td class="num">${r.areaMain!=null?esc(r.areaText):"-"}${r.landRight?`<div class="hint">대지권 ${esc(r.landRight)}</div>`:""}</td>
      <td>${esc(ownersTxt)||"-"}</td>
      <td class="ctr">${(r.mortgages||[]).length||"-"}</td>
      <td class="num">${totTxt?esc(totTxt):"-"}</td>
      <td>${esc(holders)||"-"}</td>
      <td>${esc(joints)||"-"}</td>
      <td>${warns.length?warns.map(w=>`<span class="${/부분일치|확인 필요|검토/.test(w)?'ctag':'wtag'}">${esc(w)}</span>`).join(""):'<span class="otag">특이사항 없음</span>'}</td>`;
    tr.addEventListener("click",()=>toggleDetail(tr,r));
    tb.appendChild(tr);
  });
}

function miniTable(headers, rows){
  if(!rows.length)return '<div class="hint">해당 없음</div>';
  return `<table class="mini"><tr>${headers.map(h=>`<th>${esc(h)}</th>`).join("")}</tr>${
    rows.map(cells=>`<tr>${cells.map(c=>`<td${c&&c.num?' class="num"':""}>${esc(c&&c.num!=null?c.v:c)}</td>`).join("")}</tr>`).join("")}</table>`;
}
function toggleDetail(tr,r){
  if(tr.nextSibling&&tr.nextSibling.classList&&tr.nextSibling.classList.contains("detail")){tr.nextSibling.remove();return;}
  const d=document.createElement("tr"); d.className="detail";
  // colspan: 요약 테이블 열 수 (10)
  const oRows=(r.owners||[]).map(o=>[o.name,o.role||"",o.share||"",o.regNo||"",o.cause||"",o.date||""]);
  const mRows=(r.mortgages||[]).map(m=>[m.rank,m.date,{num:1,v:fmtWon(m.maxAmt)},m.debtor,m.holder,m.joint,m.note]);
  const gRows=(r.gapRights||[]).map(g=>[g.rank,g.kind,g.date||"",g.holder||""]);
  const eRows=(r.eulOthers||[]).map(o=>[o.rank,o.kind,o.date||"",{num:1,v:fmtWon(o.amt)},o.holder||""]);
  d.innerHTML=`<td colspan="10">
    <div class="dt-sec"><h4>소유자 (갑구)</h4>${miniTable(["성명/명칭","구분","지분","등록번호","등기원인","접수일"],oRows)}</div>
    <div class="dt-sec"><h4>근저당권 (을구)</h4>${miniTable(["순위","접수일","채권최고액","채무자","근저당권자","공동담보","비고"],mRows)}</div>
    ${eRows.length?`<div class="dt-sec"><h4>전세권 등 기타 을구 권리</h4>${miniTable(["순위","종류","접수일","금액","권리자"],eRows)}</div>`:""}
    ${gRows.length?`<div class="dt-sec"><h4>갑구 권리제한 (가압류 등)</h4>${miniTable(["순위","종류","접수일","권리자"],gRows)}</div>`:""}
  </td>`;
  tr.after(d);
}

function clearAll(){
  if(!confirm("업로드한 결과를 모두 지울까요?"))return;
  RESULTS.length=0; flist.innerHTML=""; renderTable();
}

/* ================== 4. 최소 XLSX 생성기 (외부 라이브러리 없이 작성) ================== */
const _crcT=(()=>{const t=[];for(let n=0;n<256;n++){let c=n;for(let k=0;k<8;k++)c=c&1?0xEDB88320^(c>>>1):c>>>1;t[n]=c>>>0;}return t;})();
function _crc32(b){let c=0xFFFFFFFF;for(let i=0;i<b.length;i++)c=_crcT[(c^b[i])&0xFF]^(c>>>8);return(c^0xFFFFFFFF)>>>0;}
function _zip(files){
  const u16=n=>[n&255,(n>>8)&255], u32=n=>[n&255,(n>>8)&255,(n>>16)&255,(n>>24)&255];
  const parts=[], cen=[]; let off=0;
  for(const f of files){
    const nm=new TextEncoder().encode(f.name), crc=_crc32(f.data), sz=f.data.length;
    const lh=[].concat(u32(0x04034b50),u16(20),u16(0x800),u16(0),u16(0),u16(0),u32(crc),u32(sz),u32(sz),u16(nm.length),u16(0));
    parts.push(new Uint8Array(lh),nm,f.data);
    cen.push(new Uint8Array([].concat(u32(0x02014b50),u16(20),u16(20),u16(0x800),u16(0),u16(0),u16(0),u32(crc),u32(sz),u32(sz),u16(nm.length),u16(0),u16(0),u16(0),u16(0),u32(0),u32(off))),nm);
    off+=lh.length+nm.length+sz;
  }
  const cs=cen.reduce((s,c)=>s+c.length,0);
  const eocd=new Uint8Array([].concat(u32(0x06054b50),u16(0),u16(0),u16(files.length),u16(files.length),u32(cs),u32(off),u16(0)));
  return new Blob([...parts,...cen,eocd],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
}
const _xE=s=>String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
const _col=n=>{let s="";n++;while(n){s=String.fromCharCode(65+(n-1)%26)+s;n=Math.floor((n-1)/26);}return s;};
const T=(v,s=5)=>({t:1,v:v??"",s}), N=(v,s=2)=>({t:0,v:Math.round(v),s}), E=()=>({t:1,v:"",s:5});
const Nd=(v,s=12)=>({t:0,v,s});
// 색상 셀(공동담보 그룹용): k=0~5 → 텍스트 s=15+2k, 숫자 s=16+2k
const Tc=(v,k)=>({t:1,v:v??"",s:15+k*2}), Nc=(v,k)=>({t:0,v:Math.round(v),s:16+k*2});
// 면적: 정수는 소수점 없이(트레일링 닷 방지), 소수는 최대 2자리
const areaCell=v=> v==null?E() : (Number.isInteger(v)?N(v,2):Nd(v));
// WP 표준 타이틀: 남색 바(스타일 13 = 흰 글자 굵게 + 남색)를 표 폭(ncols열)만큼만 확장
const barRow=(text,ncols=13)=>[null,T(text,13),...Array.from({length:Math.max(0,ncols-1)},()=>T("",13))];
/* 데이터가 있는 모든 열의 너비 자동맞춤 (타이틀바 s=13, 메타행 s=0 은 제외) */
function autoCols(R2){
  const w=[];
  R2.forEach(row=>{ if(!row)return;
    row.forEach((c,ci)=>{ if(!c||ci===0||c.s===13||c.s===0)return;
      let s=c.t===0?Number(c.v||0).toLocaleString("en-US"):String(c.v==null?"":c.v);
      let ww=0; for(const ch of s) ww+= ch.charCodeAt(0)>0x2000?2.0:1.15;
      w[ci]=Math.max(w[ci]||0, ww);
    });
  });
  let out='<cols><col min="1" max="1" width="2.14"/>';
  for(let i=1;i<w.length;i++){
    const width=Math.min(60, Math.max(6,(w[i]||6)+2.5));
    out+=`<col min="${i+1}" max="${i+1}" width="${width.toFixed(2)}"/>`;
  }
  return out+'</cols>';
}
function _mkSheet(R2, widths, merges, views, tail, rowHt){
  let body="", maxc=0;
  R2.forEach((cells,ri)=>{
    maxc=Math.max(maxc,cells.length); let rc="";
    cells.forEach((c,ci)=>{ if(!c)return; const ref=_col(ci)+(ri+1);
      rc += c.t===0 ? `<c r="${ref}" s="${c.s}"><v>${c.v}</v></c>`
                    : `<c r="${ref}" s="${c.s}" t="inlineStr"><is><t xml:space="preserve">${_xE(c.v)}</t></is></c>`;
    });
    body+=`<row r="${ri+1}">${rc}</row>`;
  });
  // sheetFormatPr: 전 행 13.5 강제 (WP 표준 — 행높이 자동맞춤으로 들쭉날쭉해지는 것 방지)
  const mc=(merges&&merges.length)?`<mergeCells count="${merges.length}">${merges.map(m=>`<mergeCell ref="${m}"/>`).join("")}</mergeCells>`:"";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="A1:${_col(Math.max(0,maxc-1))}${R2.length||1}"/>${views||'<sheetViews><sheetView workbookViewId="0" showGridLines="0"/></sheetViews>'}<sheetFormatPr defaultRowHeight="13.5"/>${widths}<sheetData>${body}</sheetData>${mc}${tail||""}</worksheet>`;
}
const THEME_XML=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="Office 테마"><a:themeElements><a:clrScheme name="Office"><a:dk1><a:sysClr val="windowText" lastClr="000000"/></a:dk1><a:lt1><a:sysClr val="window" lastClr="FFFFFF"/></a:lt1><a:dk2><a:srgbClr val="44546A"/></a:dk2><a:lt2><a:srgbClr val="E7E6E6"/></a:lt2><a:accent1><a:srgbClr val="4472C4"/></a:accent1><a:accent2><a:srgbClr val="ED7D31"/></a:accent2><a:accent3><a:srgbClr val="A5A5A5"/></a:accent3><a:accent4><a:srgbClr val="FFC000"/></a:accent4><a:accent5><a:srgbClr val="5B9BD5"/></a:accent5><a:accent6><a:srgbClr val="70AD47"/></a:accent6><a:hlink><a:srgbClr val="0563C1"/></a:hlink><a:folHlink><a:srgbClr val="954F72"/></a:folHlink></a:clrScheme><a:fontScheme name="Office"><a:majorFont><a:latin typeface="Calibri Light"/><a:ea typeface=""/><a:cs typeface=""/><a:font script="Hang" typeface="맑은 고딕"/></a:majorFont><a:minorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/><a:font script="Hang" typeface="맑은 고딕"/></a:minorFont></a:fontScheme><a:fmtScheme name="Office"><a:fillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:fillStyleLst><a:lnStyleLst><a:ln w="6350"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln><a:ln w="12700"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln><a:ln w="19050"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln></a:lnStyleLst><a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle></a:effectStyleLst><a:bgFillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:bgFillStyleLst></a:fmtScheme></a:themeElements><a:objectDefaults/><a:extraClrSchemeLst/></a:theme>`;
function _saveWB(fname, sheets){
  const styles=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="3"><numFmt numFmtId="166" formatCode="#,##0.##"/><numFmt numFmtId="164" formatCode="yyyy\\-mm\\-dd"/><numFmt numFmtId="165" formatCode="#,##0_);[Red](#,##0);-_)"/></numFmts><fonts count="3"><font><sz val="10"/><name val="맑은 고딕"/></font><font><b/><sz val="10"/><name val="맑은 고딕"/></font><font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="맑은 고딕"/></font></fonts><fills count="13"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFACB9CA"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF7F9FB"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFEEF1F4"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FF222B35"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFDDEBF7"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFE2EFDA"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFCE4D6"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFDF0D5"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFEDE7F6"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFCE4EC"/></patternFill></fill></fills><borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"><color rgb="FFBFBFBF"/></left><right style="thin"><color rgb="FFBFBFBF"/></right><top style="thin"><color rgb="FFBFBFBF"/></top><bottom style="thin"><color rgb="FFBFBFBF"/></bottom><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="27"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="10" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/><xf numFmtId="165" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="164" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="1" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="1" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="166" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/><xf numFmtId="0" fontId="2" fillId="5" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="165" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="7" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="7" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="8" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="8" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="9" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="9" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="10" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="10" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="11" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="11" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="12" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf><xf numFmtId="165" fontId="0" fillId="12" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="center"/></xf></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
  const ct=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${sheets.map((s,ix)=>`<Override PartName="/xl/worksheets/sheet${ix+1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")}<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/><Override PartName="/xl/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/></Types>`;
  const rels=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  const wb=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${sheets.map((s,ix)=>`<sheet name="${_xE(s.name)}" sheetId="${ix+1}" r:id="rId${ix+1}"/>`).join("")}</sheets><calcPr fullCalcOnLoad="1"/></workbook>`;
  const wbr=`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${sheets.map((s,ix)=>`<Relationship Id="rId${ix+1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${ix+1}.xml"/>`).join("")}<Relationship Id="rId${sheets.length+1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/><Relationship Id="rId${sheets.length+2}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme" Target="theme/theme1.xml"/></Relationships>`;
  const enc=s=>new TextEncoder().encode(s);
  const files=[
    {name:"[Content_Types].xml",data:enc(ct)},
    {name:"_rels/.rels",data:enc(rels)},
    {name:"xl/workbook.xml",data:enc(wb)},
    {name:"xl/_rels/workbook.xml.rels",data:enc(wbr)},
    {name:"xl/styles.xml",data:enc(styles)},
    {name:"xl/theme/theme1.xml",data:enc(THEME_XML)},
  ];
  sheets.forEach((s,ix)=>files.push({name:`xl/worksheets/sheet${ix+1}.xml`,data:enc(s.xml)}));
  const a=document.createElement("a");
  a.href=URL.createObjectURL(_zip(files)); a.download=fname; a.click();
}

/* ================== 5. 엑셀 출력 ================== */
function exportXLSX(){
  if(!RESULTS.length){alert("먼저 PDF를 업로드하세요."); return;}
  const co=document.getElementById("coName").value.trim();

  /* 정렬: 1순위 소재지 가나다순, 2순위 토지 → 건물 → 집합건물 */
  const typeOrder={"토지":0,"건물":1,"집합건물":2};
  const ROWS=[...RESULTS].sort((a,b)=>{
    const c=String(a.address||a.file||"").localeCompare(String(b.address||b.file||""),"ko");
    return c||((typeOrder[a.kind]??9)-(typeOrder[b.kind]??9));
  });

  /* 공동담보 그룹 색상: 목록번호가 2건 이상 등장하는 그룹만 색칠 (같은 그룹 = 같은 색) */
  const jointKey=j=>{const m=String(j||"").match(/제?\s*[\d-]{2,}\s*호/);return m?norm(m[0]):"";};
  const jCount={};
  ROWS.forEach(r=>(r.mortgages||[]).forEach(m=>{const k=jointKey(m.joint);if(k)jCount[k]=(jCount[k]||0)+1;}));
  const jColor={}; let jn=0;
  Object.keys(jCount).forEach(k=>{ if(jCount[k]>=2){ jColor[k]=jn%6; jn++; } });

  const bar2=n=>[];   // 병합 없음 (barRow가 타이틀바 전체 열을 칠하므로 병합 불필요)

  /* 시트 1: 요약표 */
  const sumH=["No","구분","고유번호","소재지","면적(㎡)","면적 상세","대지권","소유자 (지분)","근저당 건수","채권최고액 합계","근저당권자","공동담보","권리제한 및 참고"];
  const S=[];
  S.push([]);
  S.push(barRow("등기부등본 정리표", sumH.length));
  S.push([null,T("회사명: "+(co||"미입력"),0)]);
  S.push([]);
  S.push([null,...sumH.map(h=>T(h,1))]);
  ROWS.forEach((r,i)=>{
    const totBy=amtByCurrency(r.mortgages); const curKeys=Object.keys(totBy);
    // 원화 단일이면 숫자셀(합계·서식 유지), 외화가 섞이면 통합합계 금지하고 통화별 텍스트로 표기
    const totCell = !curKeys.length ? E()
      : (curKeys.length===1&&curKeys[0]==="KRW") ? N(totBy.KRW)
      : T(fmtAmtByCur(totBy));
    const holders=[...new Set((r.mortgages||[]).map(m=>m.holder).filter(Boolean))].join(", ");
    const joints=[...new Set((r.mortgages||[]).map(m=>m.joint).filter(Boolean))].join(", ");
    const warns=[...(r.warnings||[])]; { const om=ownerMatch(r); if(om==="none")warns.unshift("소유자가 입력한 회사명과 불일치"); else if(om==="similar")warns.unshift("회사명 부분일치(확인 필요)"); }
    S.push([null, N(i+1,5), T(r.kind||"-"), T(r.uniqueNo||""), T(r.address||""),
      areaCell(r.areaMain), T(r.areaText||""), T(r.landRight||""),
      T((r.owners||[]).map(o=>`${o.name}${o.share?` (${o.share})`:""}`).join(", ")),
      N((r.mortgages||[]).length), totCell, T(holders), T(joints),
      T(warns.join(" / ")||"특이사항 없음")]);
  });
  const sumSheet=_mkSheet(S, autoCols(S), bar2(sumH.length),
    `<sheetViews><sheetView workbookViewId="0" showGridLines="0"><pane ySplit="5" topLeftCell="A6" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`);

  /* 시트 2: 근저당권 상세 (공동담보 그룹별 색상) */
  const mH=["연번","소재지","고유번호","순위번호","접수일","채권최고액","채무자","근저당권자","권리자 등록번호","공동담보","비고"];
  const M=[];
  M.push([]);
  M.push(barRow("근저당권 상세 (을구 유효사항)", mH.length));
  M.push([]);
  M.push([null,...mH.map(h=>T(h,1))]);
  let mi=0;
  ROWS.forEach(r=>(r.mortgages||[]).forEach(m=>{
    mi++;
    const k=jColor[jointKey(m.joint)];                 // undefined = 색 없음
    const t=v=>k===undefined?T(v):Tc(v,k);
    const n=v=>k===undefined?(v?N(v):E()):(v?Nc(v,k):Tc("",k));
    const num=v=>k===undefined?N(v,5):Nc(v,k);
    M.push([null, num(mi), t(r.address||r.file), t(r.uniqueNo||""), t(m.rank), t(m.date||""),
      n(m.maxAmt), t(m.debtor||""), t(m.holder||""), t(m.holderReg||""), t(m.joint||""), t(m.note||"")]);
  }));
  if(!mi)M.push([null,T("유효한 근저당권 없음")]);
  const mortSheet=_mkSheet(M, autoCols(M), bar2(mH.length));

  /* 시트 3: 소유자 상세 */
  const oH=["연번","소재지","고유번호","소유자","구분","지분","등록번호","등기원인","접수일"];
  const O=[];
  O.push([]);
  O.push(barRow("소유자 상세 (갑구 현재 유효사항)", oH.length));
  O.push([]);
  O.push([null,...oH.map(h=>T(h,1))]);
  let oi=0;
  ROWS.forEach(r=>(r.owners||[]).forEach(o=>{
    oi++;
    O.push([null, N(oi,5), T(r.address||r.file), T(r.uniqueNo||""), T(o.name), T(o.role||""),
      T(o.share||""), T(o.regNo||""), T(o.cause||""), T(o.date||"")]);
  }));
  if(!oi)O.push([null,T("인식된 소유자 없음")]);
  const ownSheet=_mkSheet(O, autoCols(O), bar2(oH.length));

  /* 시트 4: 권리제한 (갑구 가압류 등 + 을구 전세권 등) */
  const gH=["연번","소재지","구분","종류","순위번호","접수일","금액","권리자","비고"];
  const G=[];
  G.push([]);
  G.push(barRow("권리제한 및 기타 권리", gH.length));
  G.push([]);
  G.push([null,...gH.map(h=>T(h,1))]);
  let gi=0;
  ROWS.forEach(r=>{
    (r.gapRights||[]).forEach(g=>{gi++;G.push([null,N(gi,5),T(r.address||r.file),T("갑구"),T(g.kind),T(g.rank),T(g.date||""),E(),T(g.holder||""),E()]);});
    (r.eulOthers||[]).forEach(o=>{gi++;G.push([null,N(gi,5),T(r.address||r.file),T("을구"),T(o.kind),T(o.rank),T(o.date||""),o.amt?N(o.amt):E(),T(o.holder||""),E()]);});
  });
  if(!gi)G.push([null,T("해당사항 없음")]);
  const rightSheet=_mkSheet(G, autoCols(G), bar2(gH.length));

  _saveWB(`등기부등본정리_${(co||"전체").replace(/[\\/:*?"<>|]/g,"")}.xlsx`,[
    {name:"요약표",xml:sumSheet},
    {name:"근저당권 상세",xml:mortSheet},
    {name:"소유자 상세",xml:ownSheet},
    {name:"권리제한",xml:rightSheet},
  ]);
}

/* 인라인 onXXX= 핸들러를 여기로 옮김 (모듈은 전역 함수를 노출하지 않으므로) */
document.getElementById('helpBtn').addEventListener('click', ()=>toggleGuide(true));
document.getElementById('guideClose').addEventListener('click', ()=>toggleGuide(false));
document.getElementById('coName').addEventListener('input', renderTable);
document.getElementById('drop').addEventListener('click', ()=>document.getElementById('fileIn').click());
document.getElementById('clearBtn').addEventListener('click', clearAll);
document.getElementById('exportBtn').addEventListener('click', exportXLSX);

/* tests.html이 이 페이지를 iframe으로 띄워 순수 함수를 검증한다. 모듈은 전역을 만들지 않으므로
   검증 대상만 명시적으로 노출한다. 도구 동작에는 쓰이지 않는다. */
Object.assign(window, { amtByCurrency, cleanDebtor, fileStatus, fmtAmtByCur, ownerMatch, parseGapgu });
